'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useImage } from '@hooks/useImage';
import { useImageUpload } from '@hooks/useImageUpload';
import { useTeacherProfile } from '@hooks/useTeacherProfile';
import Email from '@icons/Email.svg';
import Location from '@icons/Location.svg';
import Phone from '@icons/Phone.svg';
import { TeacherProfile as TeacherProfileType } from 'types/collections';
import { ROLES } from 'types/roles';

import { Button } from '@components/Button';
import { Header, PageContent, PageGrid, SectionBigGap } from '@components/Common';
import { LoadingErrorHandler } from '@components/LoadingErrorHandler';
import { ProfileActions, ProfileInfoBlock, ProfileInfoCard } from '@components/Profile/components';
import { ProfileInfoTitle, TitleInfoContainer } from '@components/Profile/styles';
import { showToast } from '@components/ToastContainer';

/**
 * Teacher Profile component displays the teacher's profile information and
 * allows them to update their profile picture, reset password and delete their account.
 */
export const TeacherProfile: React.FC = () => {
  const { t } = useTranslation('common');
  const { teacher, updateTeacherProfile, requestAccountDeletion, isPending, isError } =
    useTeacherProfile();
  const { imageUrl, imageLoading } = useImage(teacher?.avatar_url);

  const onImageUpload = (newPath: string) => {
    updateTeacherProfile({ avatar_url: newPath });
    showToast('success', t('toast.success'), t('toast.successPhotoUpload'));
  };

  const { loading, uploading, fileInputRef, handleFileChange, triggerFileSelect } = useImageUpload({
    onImageUpload,
    errorTitle: t('photoErrorTitle'),
  });

  return (
    <PageGrid className="full-height" $marginTopLarge={false}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <PageContent>
        <LoadingErrorHandler isLoading={isPending} isError={isError}>
          <Header>{t('menu.profile')}</Header>
          <TitleInfoContainer>
            <ProfileInfoCard
              firstName={teacher?.first_name}
              secondName={teacher?.second_name}
              imageUrl={imageUrl}
              imageLoading={imageLoading}
            />
            <Button
              label={t('updateFoto')}
              type="button"
              variant="secondary"
              onClick={triggerFileSelect}
              disabled={uploading || loading}
            />
          </TitleInfoContainer>
          <SectionBigGap>
            <TitleInfoContainer>
              <ProfileInfoTitle>{t('contact')}</ProfileInfoTitle>
              <ProfileInfoBlock label={t('address')} value={teacher?.address} icon={<Location />} />
              <ProfileInfoBlock label={t('emailShort')} value={teacher?.email} icon={<Email />} />
              <ProfileInfoBlock label={t('phone')} value={teacher?.phone_number} icon={<Phone />} />
            </TitleInfoContainer>
            <ProfileActions<Partial<TeacherProfileType>>
              role={ROLES.TEACHER}
              deleteRequest={requestAccountDeletion}
              updateProfile={updateTeacherProfile}
            />
          </SectionBigGap>
        </LoadingErrorHandler>
      </PageContent>
    </PageGrid>
  );
};
